import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary } from '@shared/ui';
import { captureError } from '@shared/lib/observability';

type ReasonFilter = 'all' | 'suffix_variant' | 'trgm_name' | 'shared_ig_handle';
type AdvFilter = 'all' | 'same_advertiser' | 'both_unlinked' | 'different_advertiser';

const PAGE_SIZE = 30;

interface DupePair {
  id_a: string;
  name_a: string;
  adv_a: string | null;
  ig_handle_a: string | null;
  source_a: string;
  id_b: string;
  name_b: string;
  adv_b: string | null;
  ig_handle_b: string | null;
  source_b: string;
  confidence: number;
  reason: string;
  advertiser_state: string;
}

const REASON_LABEL: Record<string, string> = {
  suffix_variant: 'Suffix variant',
  trgm_name: 'Similar name',
  shared_ig_handle: 'Same IG',
};
const REASON_COLOR: Record<string, string> = {
  suffix_variant: '#f59e0b',
  trgm_name: '#4a9eff',
  shared_ig_handle: '#e84393',
};
const ADV_COLOR: Record<string, string> = {
  same_advertiser: '#22c55e',
  both_unlinked: '#888',
  different_advertiser: '#f97316',
};
const ADV_LABEL: Record<string, string> = {
  same_advertiser: 'Same advertiser',
  both_unlinked: 'Both unlinked',
  different_advertiser: 'Diff advertiser',
};

export default function BrandDuplicates() {
  const invalidate = useInvalidateSupabase();
  const [params, setParams] = useSearchParams();
  const reason = (params.get('reason') as ReasonFilter) || 'all';
  const advFilter = (params.get('adv') as AdvFilter) || 'all';
  const page = parseInt(params.get('page') ?? '0', 10) || 0;
  const setParam = (key: string, val: string) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  };
  const setReason = (v: ReasonFilter) => setParam('reason', v === 'all' ? '' : v);
  const setAdvFilter = (v: AdvFilter) => setParam('adv', v === 'all' ? '' : v);
  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p === 0) next.delete('page'); else next.set('page', String(p));
    setParams(next, { replace: false });
  };
  const [busyPair, setBusyPair] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const queryKey = ['admin-brand-dupes', reason, advFilter, page];

  const dupesQ = useSupabaseQuery<{ rows: DupePair[]; total: number }>(
    queryKey,
    async () => {
      let qb = supabase
        .from('v_brand_dupe_candidates_active')
        .select('*', { count: 'exact' });

      if (reason !== 'all') qb = qb.eq('reason', reason);
      if (advFilter !== 'all') qb = qb.eq('advertiser_state', advFilter);

      qb = qb
        .order('confidence', { ascending: false })
        .order('id_a')
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      const { data, error, count } = await qb;
      if (error) return { data: null, error } as any;
      return { data: { rows: (data ?? []) as DupePair[], total: count ?? 0 }, error: null } as any;
    },
  );

  const pairs = dupesQ.data?.rows ?? [];
  const total = dupesQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const pairKey = (a: string, b: string) => `${a}:${b}`;

  const handleMerge = async (winnerId: string, loserId: string, pairId: string) => {
    setBusyPair(pairId);
    setErrorMsg(null);
    const { error } = await supabase.rpc('merge_brands', {
      p_winner_id: winnerId,
      p_loser_id: loserId,
    });
    if (error) {
      captureError(error, { action: 'merge_brands' });
      setErrorMsg(error.message);
    } else {
      invalidate(queryKey);
    }
    setBusyPair(null);
  };

  const handleDismiss = async (idA: string, idB: string, pairId: string) => {
    setBusyPair(pairId);
    setErrorMsg(null);
    const a = idA < idB ? idA : idB;
    const b = idA < idB ? idB : idA;
    const { error } = await supabase.from('brand_dupe_dismissals').insert({
      id_a: a,
      id_b: b,
      dismissed_by: (await supabase.auth.getUser()).data.user?.id,
    });
    if (error) {
      captureError(error, { action: 'brand_dupe_dismissals.insert' });
      setErrorMsg(error.message);
    } else {
      invalidate(queryKey);
    }
    setBusyPair(null);
  };

  const pill = (label: string, active: boolean, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="admin-pill"
      style={{
        cursor: 'pointer',
        background: active ? 'rgba(74,158,255,0.18)' : 'transparent',
        border: '1px solid ' + (active ? '#4a9eff' : 'rgba(15,45,100,0.4)'),
        color: active ? '#4a9eff' : '#aaa',
        padding: '5px 10px', borderRadius: 6, fontSize: 'var(--fs-small)',
      }}
    >
      {label}
    </button>
  );

  return (
    <SectionErrorBoundary name="brand-duplicates">
      <p className="admin-page-sub" style={{ marginBottom: 14 }}>
        Pairs of brands that look like duplicates. Merge keeps the winner and reassigns all
        social and web-ad data. Dismiss hides the pair permanently.
      </p>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {pill('All reasons', reason === 'all', () => { setReason('all'); setPage(0); })}
          {pill('Suffix variant', reason === 'suffix_variant', () => { setReason('suffix_variant'); setPage(0); })}
          {pill('Similar name', reason === 'trgm_name', () => { setReason('trgm_name'); setPage(0); })}
          {pill('Same IG', reason === 'shared_ig_handle', () => { setReason('shared_ig_handle'); setPage(0); })}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {pill('Any advertiser', advFilter === 'all', () => { setAdvFilter('all'); setPage(0); })}
          {pill('Same advertiser', advFilter === 'same_advertiser', () => { setAdvFilter('same_advertiser'); setPage(0); })}
          {pill('Both unlinked', advFilter === 'both_unlinked', () => { setAdvFilter('both_unlinked'); setPage(0); })}
          {pill('Diff advertiser', advFilter === 'different_advertiser', () => { setAdvFilter('different_advertiser'); setPage(0); })}
        </div>
        <span style={{ fontSize: 'var(--fs-small)', color: '#888', marginLeft: 'auto' }}>
          {dupesQ.isLoading ? '…' : `${total.toLocaleString()} pairs`}
        </span>
      </div>

      {errorMsg && (
        <div style={{ color: 'var(--danger)', marginBottom: 10, fontSize: 'var(--fs-body)' }}>{errorMsg}</div>
      )}

      {dupesQ.isLoading && <Skeleton height={400} />}
      {dupesQ.error && (
        <div style={{ color: 'var(--danger)' }}>
          {String((dupesQ.error as Error)?.message ?? dupesQ.error)}
        </div>
      )}

      {!dupesQ.isLoading && !dupesQ.error && pairs.length === 0 && (
        <div className="admin-empty">No duplicate candidates match the current filter.</div>
      )}

      {!dupesQ.isLoading && pairs.length > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pairs.map((p) => {
              const pid = pairKey(p.id_a, p.id_b);
              const busy = busyPair === pid;
              return (
                <div
                  key={pid}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(15,45,100,0.4)',
                    borderRadius: 8,
                    padding: '12px 14px',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  {/* Left brand */}
                  <div>
                    <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: '#e8eaed' }}>{p.name_a}</div>
                    <div style={{ fontSize: 'var(--fs-caption)', color: '#666', marginTop: 3 }}>{p.source_a}</div>
                    {p.ig_handle_a && (
                      <a
                        href={`https://instagram.com/${p.ig_handle_a}`}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 'var(--fs-caption)', color: '#e84393', textDecoration: 'none', marginTop: 2, display: 'block' }}
                      >
                        @{p.ig_handle_a} ↗
                      </a>
                    )}
                    {p.adv_a && (
                      <div style={{ fontSize: 'var(--fs-caption)', color: '#22c55e', marginTop: 2 }}>linked to advertiser</div>
                    )}
                  </div>

                  {/* Center: badges + actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 160 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                      <span style={{
                        fontSize: 'var(--fs-caption)', padding: '2px 7px', borderRadius: 10,
                        background: REASON_COLOR[p.reason] + '22',
                        color: REASON_COLOR[p.reason],
                        border: `1px solid ${REASON_COLOR[p.reason]}44`,
                      }}>
                        {REASON_LABEL[p.reason] ?? p.reason}
                      </span>
                      <span style={{
                        fontSize: 'var(--fs-caption)', padding: '2px 7px', borderRadius: 10,
                        background: ADV_COLOR[p.advertiser_state] + '22',
                        color: ADV_COLOR[p.advertiser_state],
                        border: `1px solid ${ADV_COLOR[p.advertiser_state]}44`,
                      }}>
                        {ADV_LABEL[p.advertiser_state] ?? p.advertiser_state}
                      </span>
                      <span style={{
                        fontSize: 'var(--fs-caption)', padding: '2px 7px', borderRadius: 10,
                        background: 'rgba(255,255,255,0.06)', color: '#aaa',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}>
                        {Math.round(p.confidence * 100)}%
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleMerge(p.id_a, p.id_b, pid)}
                        title={`Keep "${p.name_a}", delete "${p.name_b}"`}
                        style={{
                          padding: '4px 10px', fontSize: 'var(--fs-small)', borderRadius: 5, cursor: busy ? 'default' : 'pointer',
                          background: 'rgba(74,158,255,0.15)', border: '1px solid #4a9eff44', color: '#4a9eff',
                          opacity: busy ? 0.5 : 1,
                        }}
                      >
                        ← Keep
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleDismiss(p.id_a, p.id_b, pid)}
                        title="Not a duplicate — hide this pair"
                        style={{
                          padding: '4px 10px', fontSize: 'var(--fs-small)', borderRadius: 5, cursor: busy ? 'default' : 'pointer',
                          background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#888',
                          opacity: busy ? 0.5 : 1,
                        }}
                      >
                        {busy ? '…' : 'Skip'}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleMerge(p.id_b, p.id_a, pid)}
                        title={`Keep "${p.name_b}", delete "${p.name_a}"`}
                        style={{
                          padding: '4px 10px', fontSize: 'var(--fs-small)', borderRadius: 5, cursor: busy ? 'default' : 'pointer',
                          background: 'rgba(74,158,255,0.15)', border: '1px solid #4a9eff44', color: '#4a9eff',
                          opacity: busy ? 0.5 : 1,
                        }}
                      >
                        Keep →
                      </button>
                    </div>
                  </div>

                  {/* Right brand */}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: '#e8eaed' }}>{p.name_b}</div>
                    <div style={{ fontSize: 'var(--fs-caption)', color: '#666', marginTop: 3 }}>{p.source_b}</div>
                    {p.ig_handle_b && (
                      <a
                        href={`https://instagram.com/${p.ig_handle_b}`}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 'var(--fs-caption)', color: '#e84393', textDecoration: 'none', marginTop: 2, display: 'block' }}
                      >
                        @{p.ig_handle_b} ↗
                      </a>
                    )}
                    {p.adv_b && (
                      <div style={{ fontSize: 'var(--fs-caption)', color: '#22c55e', marginTop: 2 }}>linked to advertiser</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 16 }}>
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage(Math.max(0, page - 1))}
              className="admin-btn-merge"
              style={{ padding: '6px 14px', fontSize: 'var(--fs-small)' }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: 'var(--fs-small)', color: '#888' }}>
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage(page + 1)}
              className="admin-btn-merge"
              style={{ padding: '6px 14px', fontSize: 'var(--fs-small)' }}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </SectionErrorBoundary>
  );
}
